import React, { useState, useEffect } from 'react';
import { useReactiveVar } from '@apollo/client';

import Page from '@/components/Page';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Stack from '@/components/Stack';
import { Body, Heading } from '@/components/Typography';
import Chip from '@/components/Chip';
import Button from '@/components/Button';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { format } from 'date-fns';
import { UserRoleEnum } from '@/types/graphql';
import { currentAdminVar } from '@/util/apollo/cache';
import Pagination from '@/components/Pagination';

// Components
import WorkerDetailModal from './components/WorkerDetailModal';
import BypassWorkerModal from './components/BypassWorkerModal';
import AuditHistoryModal from './components/AuditHistoryModal';

// Import types
import { Worker, OnboardingDashboardData } from '@/types/onboardingBypass';

type PaginationData = {
  total_count: number;
  current_page: number;
  per_page: number;
  total_pages: number;
};

// I-9 Status options
const I9_STATUS_OPTIONS = [
  { value: '', label: 'All I-9 Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'needs_attention', label: 'Needs Attention' },
  { value: 'tentative_nc', label: 'Tentative NC' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'pending_e-verify', label: 'Pending E-Verify' },
  { value: 'pending_section2', label: 'Pending Section 2' },
  { value: 'e-verify_in_progress', label: 'E-Verify In Progress' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'closed', label: 'Closed' },
  { value: 'n/a', label: 'N/A' },
];

// Bypassed filter options
const BYPASSED_OPTIONS = [
  { value: '', label: 'All Workers' },
  { value: 'true', label: 'Bypassed Only' },
  { value: 'false', label: 'Not Bypassed' },
];

const formatDate = (dateString?: string) => {
  if (!dateString) return 'N/A';
  try {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  } catch (e) {
    return dateString;
  }
};

const getStatusChip = (status?: string) => {
  if (!status) return <Chip label="Unknown" size="small" />;

  switch (status.toLowerCase()) {
    case 'onboarding_completed':
      return <Chip color="success" label="Completed" size="small" />;
    case 'onboarding_in_progress':
      return <Chip color="warning" label="In Progress" size="small" />;
    case 'pending_ssn':
      return <Chip color="warning" label="Pending SSN" size="small" />;
    case 'pending_workplace':
      return <Chip color="warning" label="Pending Workplace" size="small" />;
    case 'pending_tax_forms':
      return <Chip color="warning" label="Pending Tax Forms" size="small" />;
    default:
      return <Chip label={status.replace(/_/g, ' ')} size="small" />;
  }
};

const OnboardingBypass: React.FC = () => {
  const currentAdmin = useReactiveVar(currentAdminVar);
  // BH-28: every tenant admin may use this page (Product, Jira 61111). Keyed on
  // `user.roleId`, the same field PrivateRoute uses. `currentAdmin.role` is
  // graphy's TenantRole level (MANAGER 0, ANALYST 1, ADMIN 2), so comparing it
  // with `Role.TENANT_ADMIN` (1) let only ANALYSTs in.
  const isTenantAdmin =
    currentAdmin?.user?.roleId === UserRoleEnum.TENANT_ADMIN;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<OnboardingDashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [i9StatusFilter, setI9StatusFilter] = useState<string>('');
  const [bypassedFilter, setBypassedFilter] = useState<string>('');
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check localStorage on component initialization
    return localStorage.getItem('onboardingBypassAuth') === 'true';
  });
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [pagination, setPagination] = useState<PaginationData>({
    total_count: 0,
    current_page: 1,
    per_page: 50, // Match backend default
    total_pages: 0,
  });

  // Modal states
  const [selectedWorker, setSelectedWorker] = useState<Worker | null>(null);
  const [showWorkerDetail, setShowWorkerDetail] = useState(false);
  const [showBypassModal, setShowBypassModal] = useState(false);
  const [showAuditHistory, setShowAuditHistory] = useState(false);

  // Authentication handler
  const handlePasswordSubmit = (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (password === 'gravydevs123') {
      setIsAuthenticated(true);
      setAuthError('');
      // Save authentication state to localStorage
      localStorage.setItem('onboardingBypassAuth', 'true');
    } else {
      setAuthError('Incorrect password');
      setPassword('');
    }
  };

  // BH5-13: no dashboard request while the page shows Access Denied or the
  // password screen.
  const canView = isTenantAdmin && isAuthenticated;

  // Debounce search with 500ms delay
  useEffect(() => {
    if (!canView) return undefined;
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
        setPagination((prev) => ({ ...prev, current_page: 1 })); // Reset to first page on search
        fetchDashboardData(searchInput, 1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [canView, searchInput, searchTerm]);

  // Loads on unlock and resets to the first page when filters change.
  useEffect(() => {
    if (!canView) return;
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    fetchDashboardData(searchTerm, 1);
  }, [canView, i9StatusFilter, bypassedFilter]);

  const fetchDashboardData = async (searchQuery?: string, page: number = 1) => {
    setLoading(true);
    try {
      const params: any = {
        search: searchQuery || searchTerm,
        page,
        per_page: pagination.per_page,
      };

      // Add filters if they have values
      if (i9StatusFilter) {
        params.i9_status = i9StatusFilter;
      }
      if (bypassedFilter) {
        params.bypassed_only = bypassedFilter;
      }

      const response = await axiosRubyClient.get('/api/onboarding/dashboard', {
        params,
      });

      // Check if response has success flag and data structure matches backend
      if (response.data.success && response.data.data) {
        const workers = response.data.data.workers || [];
        const paginationData = response.data.data.pagination;

        const dashboardData: OnboardingDashboardData = {
          workers,
          total_workers: paginationData?.total_count || workers.length,
          bypassed_workers: workers.filter((w: any) => w.bypass_info).length,
          bypass_percentage: paginationData?.total_count
            ? (workers.filter((w: any) => w.bypass_info).length /
                paginationData.total_count) *
              100
            : 0,
        };

        setData(dashboardData);

        // Update pagination data from response
        if (paginationData) {
          setPagination({
            total_count: paginationData.total_count,
            current_page: paginationData.current_page,
            per_page: paginationData.per_page,
            total_pages: paginationData.total_pages,
          });
        }

        setError(null);
      } else {
        setError('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error fetching onboarding dashboard data:', error);
      setError('Error loading onboarding dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleWorkerClick = (worker: Worker) => {
    setSelectedWorker(worker);
    setShowWorkerDetail(true);
  };

  const handleBypassClick = (worker?: Worker) => {
    if (worker) {
      setSelectedWorker(worker);
    }
    setShowWorkerDetail(false);
    setShowBypassModal(true);
  };

  const handleBypassSuccess = () => {
    fetchDashboardData();
  };

  const handleShowAuditHistory = (worker?: Worker) => {
    if (worker) {
      setSelectedWorker(worker);
    }
    setShowAuditHistory(true);
  };

  const closeModals = () => {
    setShowWorkerDetail(false);
    setShowBypassModal(false);
    setShowAuditHistory(false);
    setSelectedWorker(null);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    fetchDashboardData(searchInput, 1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    fetchDashboardData('', 1);
  };

  const handleClearFilters = () => {
    setI9StatusFilter('');
    setBypassedFilter('');
    setSearchInput('');
    setSearchTerm('');
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    fetchDashboardData('', 1);
  };

  const onPageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
    fetchDashboardData(searchTerm, page);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('onboardingBypassAuth');
  };

  // Check if user is tenant admin
  if (!isTenantAdmin) {
    return (
      <Page size="full" title="Access Denied">
        <Card>
          <Card.Section>
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <Body>
                You do not have permission to access this page. Only tenant
                administrators can manage onboarding bypass.
              </Body>
            </div>
          </Card.Section>
        </Card>
      </Page>
    );
  }

  // Authentication check
  if (!isAuthenticated) {
    return (
      <Page size="full" title="Authentication Required">
        <Stack align="center" justify="center" style={{ padding: '40px 0' }}>
          <Card title="Enter Password">
            <Card.Section>
              <form>
                <Stack gap={16} vertical>
                  <div>
                    <label
                      htmlFor="password"
                      style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: 500,
                      }}
                    >
                      Password:
                    </label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handlePasswordSubmit();
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #e1e5e9',
                        borderRadius: '4px',
                        fontSize: '14px',
                      }}
                      placeholder="Enter password"
                      required
                    />
                  </div>
                  {authError && (
                    <div style={{ color: '#dc3545', fontSize: '14px' }}>
                      {authError}
                    </div>
                  )}
                  <Button
                    onClick={handlePasswordSubmit}
                    type="submit"
                    a11yLabel="Submit Password"
                  >
                    Submit
                  </Button>
                </Stack>
              </form>
            </Card.Section>
          </Card>
        </Stack>
      </Page>
    );
  }

  if (loading) {
    return (
      <Page size="full" title="Onboarding Management">
        <Stack align="center" justify="center" style={{ padding: '40px 0' }}>
          <LoadingState overlayColor="white" text="Loading onboarding data" />
        </Stack>
      </Page>
    );
  }

  if (error) {
    return (
      <Page size="full" title="Onboarding Management">
        <Card>
          <Card.Section>
            <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
              <Body>{error}</Body>
              <div style={{ marginTop: '16px' }}>
                <Button
                  a11yLabel="Try Again"
                  onClick={() =>
                    fetchDashboardData(searchTerm, pagination.current_page)
                  }
                >
                  Try Again
                </Button>
              </div>
            </div>
          </Card.Section>
        </Card>
      </Page>
    );
  }

  if (!data) {
    return (
      <Page size="full" title="Onboarding Management">
        <Card>
          <Card.Section>
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <Body>No onboarding data available</Body>
              <div style={{ marginTop: '16px' }}>
                <Button
                  a11yLabel="Refresh"
                  onClick={() =>
                    fetchDashboardData(searchTerm, pagination.current_page)
                  }
                >
                  Refresh
                </Button>
              </div>
            </div>
          </Card.Section>
        </Card>
      </Page>
    );
  }

  const workers = data.workers || [];
  const totalWorkers = data.total_workers || 0;
  const bypassedWorkers = data.bypassed_workers || 0;
  const bypassPercentage = data.bypass_percentage || 0;

  return (
    <>
      <Page
        size="full"
        title="Onboarding Management"
        primaryAction={{
          a11yLabel: 'Refresh',
          label: 'Refresh',
          onAction: () =>
            fetchDashboardData(searchTerm, pagination.current_page),
        }}
        secondaryActions={[
          {
            a11yLabel: 'Logout',
            label: 'Logout',
            onAction: handleLogout,
          },
        ]}
      >
        <Stack gap={16} vertical>
          {/* Filters Section */}
          <Card title="Filters">
            <Card.Section>
              <Stack gap={16} vertical>
                <Stack gap={16}>
                  <div style={{ flex: 1 }}>
                    <label
                      htmlFor="i9-status-filter"
                      style={{
                        display: 'block',
                        marginBottom: '4px',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      I-9 Status:
                    </label>
                    <select
                      id="i9-status-filter"
                      value={i9StatusFilter}
                      onChange={(e) => setI9StatusFilter(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #e1e5e9',
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                      }}
                    >
                      {I9_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label
                      htmlFor="bypassed-filter"
                      style={{
                        display: 'block',
                        marginBottom: '4px',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      Bypass Status:
                    </label>
                    <select
                      id="bypassed-filter"
                      value={bypassedFilter}
                      onChange={(e) => setBypassedFilter(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #e1e5e9',
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                      }}
                    >
                      {BYPASSED_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </Stack>
                {(i9StatusFilter || bypassedFilter || searchInput) && (
                  <div>
                    <button
                      type="button"
                      onClick={handleClearFilters}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      Clear All Filters
                    </button>
                  </div>
                )}
              </Stack>
            </Card.Section>
          </Card>

          {/* Search Section */}
          <Card title="Search Workers">
            <Card.Section>
              <form
                onSubmit={handleSearch}
                style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
              >
                <input
                  type="text"
                  value={searchInput}
                  onChange={handleSearchChange}
                  placeholder="Search by name, email, or ID..."
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: '1px solid #e1e5e9',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                  }}
                />
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Search
                </button>
                {searchInput && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Clear
                  </button>
                )}
              </form>
            </Card.Section>
          </Card>

          {/* Workers Table */}
          <Card
            title={`Workers (${workers.length} found)${searchTerm ? ` - Search: "${searchTerm}"` : ''}`}
          >
            <Card.Section>
              {workers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <Body color="lighter">No workers found</Body>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      backgroundColor: 'white',
                      border: '1px solid #e1e5e9',
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderBottom: '2px solid #e1e5e9',
                        }}
                      >
                        <th
                          style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            fontWeight: 600,
                            fontSize: '14px',
                          }}
                        >
                          Worker
                        </th>
                        <th
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            fontWeight: 600,
                            fontSize: '14px',
                          }}
                        >
                          Status
                        </th>
                        <th
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            fontWeight: 600,
                            fontSize: '14px',
                          }}
                        >
                          I-9 Status
                        </th>
                        <th
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            fontWeight: 600,
                            fontSize: '14px',
                          }}
                        >
                          Tax Type
                        </th>
                        <th
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            fontWeight: 600,
                            fontSize: '14px',
                          }}
                        >
                          Bypass Date
                        </th>
                        <th
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            fontWeight: 600,
                            fontSize: '14px',
                          }}
                        >
                          Bypassed
                        </th>
                        <th
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            fontWeight: 600,
                            fontSize: '14px',
                          }}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {workers.map((worker, index) => {
                        const workerName = worker.name || 'Unknown Worker';
                        const workerEmail = worker.email || '';
                        const taxType = worker.tax_type || 'Unknown';
                        const i9Status =
                          (worker as any).i9_status || 'Not Started';
                        const bypassDate = worker.bypass_info?.bypassed_at;

                        return (
                          <tr
                            key={worker.id || index}
                            style={{
                              borderBottom: '1px solid #e1e5e9',
                              backgroundColor:
                                index % 2 === 0 ? 'white' : '#f8f9fa',
                            }}
                          >
                            <td style={{ padding: '12px 16px' }}>
                              <div>
                                <div
                                  style={{
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    marginBottom: '2px',
                                    cursor: 'pointer',
                                    color: '#007bff',
                                  }}
                                  onClick={() => handleWorkerClick(worker)}
                                >
                                  {workerName}
                                </div>
                                <div
                                  style={{ fontSize: '12px', color: '#6c757d' }}
                                >
                                  {workerEmail}
                                </div>
                              </div>
                            </td>
                            <td
                              style={{
                                padding: '12px 16px',
                                textAlign: 'center',
                              }}
                            >
                              {getStatusChip(worker.onboarding_status)}
                            </td>
                            <td
                              style={{
                                padding: '12px 16px',
                                textAlign: 'center',
                              }}
                            >
                              <Chip
                                color={
                                  i9Status === 'approved'
                                    ? 'success'
                                    : i9Status === 'pending'
                                      ? 'warning'
                                      : i9Status === 'rejected'
                                        ? 'error'
                                        : 'default'
                                }
                                label={i9Status
                                  .replace(/_/g, ' ')
                                  .toUpperCase()}
                                size="small"
                              />
                            </td>
                            <td
                              style={{
                                padding: '12px 16px',
                                textAlign: 'center',
                              }}
                            >
                              <Chip
                                color={
                                  taxType === 'W2'
                                    ? 'success'
                                    : taxType === '1099'
                                      ? 'warning'
                                      : 'default'
                                }
                                label={taxType}
                                size="small"
                              />
                            </td>
                            <td
                              style={{
                                padding: '12px 16px',
                                textAlign: 'center',
                              }}
                            >
                              {bypassDate ? (
                                <div style={{ fontSize: '12px' }}>
                                  {formatDate(bypassDate)}
                                </div>
                              ) : (
                                <span
                                  style={{ color: '#6c757d', fontSize: '12px' }}
                                >
                                  -
                                </span>
                              )}
                            </td>
                            <td
                              style={{
                                padding: '12px 16px',
                                textAlign: 'center',
                              }}
                            >
                              {worker.bypass_info ? (
                                <Chip color="error" label="Yes" size="small" />
                              ) : (
                                <Chip label="No" size="small" />
                              )}
                            </td>
                            <td
                              style={{
                                padding: '12px 16px',
                                textAlign: 'center',
                              }}
                            >
                              <button
                                onClick={() => handleWorkerClick(worker)}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '12px',
                                  backgroundColor: '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                }}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {!loading && workers.length > 0 && pagination.total_pages > 1 && (
                <div style={{ marginTop: '20px' }}>
                  <Stack justify="center">
                    <Pagination
                      currentPage={pagination.current_page}
                      totalPages={pagination.total_pages}
                      onPageChange={onPageChange}
                    />
                  </Stack>
                </div>
              )}
            </Card.Section>
          </Card>
        </Stack>
      </Page>

      {/* Modals */}
      {selectedWorker && (
        <WorkerDetailModal
          worker={selectedWorker}
          open={showWorkerDetail}
          onClose={closeModals}
          onBypassClick={() => handleBypassClick(selectedWorker)}
        />
      )}

      <BypassWorkerModal
        worker={selectedWorker}
        open={showBypassModal}
        onClose={closeModals}
        onSuccess={handleBypassSuccess}
      />

      <AuditHistoryModal
        open={showAuditHistory}
        onClose={closeModals}
        workerId={selectedWorker?.id}
        workerName={selectedWorker?.name}
      />
    </>
  );
};

export default OnboardingBypass;
